import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Flex,
  HStack,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tag,
  TagLabel,
  Tabs,
  Td,
  Text,
  Tr,
  useDisclosure,
  useToast,
  Wrap,
  WrapItem
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon, EditIcon } from "@chakra-ui/icons";
import { FaUsers, FaClipboardList } from "react-icons/fa";
import {
  addClientComment,
  deleteClientContact,
  getClient,
  getClientComments,
  listClientContacts,
  listClientNeeds
} from "../../../api/clientsApi";
import ThreadedComments from "../../../components/Portal/ThreadedComments";
import ConfirmDialog from "../../../components/Portal/ConfirmDialog";
import { listMentionableUsers } from "../../../api/authApi";
import { useBusinessUnit } from "../../../context/BusinessUnitContext";
import { PLATFORM_ROLES, BU_ROLES } from "../../../constants/roles";
import {
  CLIENT_STATUS_COLORS,
  CLIENT_STATUS_LABELS,
  CLIENT_STATUS_LABEL_KEYS,
  CONTACT_STATUS_COLORS,
  CONTACT_STATUS_LABELS,
  CONTACT_STATUS_LABEL_KEYS,
  getStatusLabel
} from "../../../constants/statuses";
import {
  DataTable,
  StatusBadge,
  MiniIconButton,
  EmptyState,
  FilterBar,
  FilterSearch,
  TablePagination,
  FieldLabel,
} from "../../../components/Portal/ui";
import {
  MODAL_CONTENT_SX,
  MODAL_HEADER_SX,
  MODAL_FOOTER_SX,
} from "../../../components/Portal/portalModalChrome";
import ContactFormModal from "./ContactFormModal";
import { useTranslation } from "react-i18next";
import {
  familyColor,
  MAX_TOP_SKILLS_DETAIL,
  normalizeTopSkills,
} from "../../../utils/pilotSkills";

const normalizeSkillsValue = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((v) => (v != null ? String(v).trim() : ""))
      .filter(Boolean)
      .join(", ");
  }
  if (typeof value === "string") {
    return value.trim();
  }
  return "";
};

const FieldValue = ({ children, ...rest }) => (
  <Text fontSize="13px" fontWeight="600" color="portal.text" sx={{ marginBottom: "0 !important" }} {...rest}>
    {children}
  </Text>
);

// Navy line-tabs matching the template tab strip (active underline + navy text).
const TAB_LIST_SX = {
  borderBottomWidth: "1px",
  borderColor: "portal.line",
  "& button": {
    fontSize: "13px",
    fontWeight: 700,
    color: "portal.muted",
    px: "14px",
    py: "10px",
    borderBottom: "2px solid transparent",
    marginBottom: "-1px",
  },
  "& button[aria-selected=true]": {
    color: "portal.primary",
    borderColor: "portal.primary",
  },
};

const ClientDetailModal = ({ isOpen, onClose, clientId, initialTabIndex = 0, shouldNavigateOnClose = true }) => {
  const toast = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation("portal");
  const { effectiveRole, selectedBusinessUnitId, isLoaded: isBUContextLoaded } =
    useBusinessUnit();

  const [client, setClient] = useState(null);
  const [loadingClient, setLoadingClient] = useState(true);
  const [tabIndex, setTabIndex] = useState(initialTabIndex);

  const skillsTechnicalRaw = client?.skillsTechnical ?? client?.skills_technical;
  const skillsFunctionalRaw = client?.skillsFunctional ?? client?.skills_functional;
  const skillsBusinessRaw = client?.skillsBusiness ?? client?.skills_business;

  const skillsTechnicalText = normalizeSkillsValue(skillsTechnicalRaw);
  const skillsFunctionalText = normalizeSkillsValue(skillsFunctionalRaw);
  const skillsBusinessText = normalizeSkillsValue(skillsBusinessRaw);

  const hasSkillsTechnical = Boolean(skillsTechnicalText);
  const hasSkillsFunctional = Boolean(skillsFunctionalText);
  const hasSkillsBusiness = Boolean(skillsBusinessText);

  // Top-skills (cached aggregate across all linked needs). Each entry
  // carries its dominant skill-family so the chip can be color-coded the
  // same way as elsewhere in the app (see utils/pilotSkills.js).
  const topSkills = useMemo(
    () => normalizeTopSkills(client?.topSkills ?? client?.top_skills),
    [client?.topSkills, client?.top_skills]
  );

  // Comments
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [mentionableUsers, setMentionableUsers] = useState([]);

  // Contacts
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const createContactModal = useDisclosure();
  const editContactModal = useDisclosure();
  const deleteContactDialog = useDisclosure();
  const [deleteContactId, setDeleteContactId] = useState(null);
  const [editingContact, setEditingContact] = useState(null);

  // Needs
  const [needs, setNeeds] = useState([]);
  const [loadingNeeds, setLoadingNeeds] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");

  const canDeleteContact = useMemo(() => {
    // BU Manager is admin-equivalent (same as BU Admin / Company Admin).
    return (
      effectiveRole === PLATFORM_ROLES.COMPANY_ADMIN ||
      effectiveRole === BU_ROLES.ADMIN ||
      effectiveRole === BU_ROLES.MANAGER
    );
  }, [effectiveRole]);

  const fetchClient = useCallback(async () => {
    if (!clientId) return;
    setLoadingClient(true);
    try {
      const res = await getClient(clientId, {
        buId: selectedBusinessUnitId || undefined
      });
      setClient(res);
    } catch (e) {
      toast({
        title: t("pilotClients.detail.toasts.loadClientFailed"),
        status: "error"
      });
      setClient(null);
    } finally {
      setLoadingClient(false);
    }
  }, [clientId, selectedBusinessUnitId, toast, t]);

  const fetchComments = useCallback(async () => {
    if (!clientId) return;
    setLoadingComments(true);
    try {
      const res = await getClientComments(clientId, {
        buId: selectedBusinessUnitId || undefined
      });
      setComments(res || []);
    } catch (e) {
      // keep silent (same as needs; avoids noisy toasts on open)
      setComments([]);
    } finally {
      setLoadingComments(false);
    }
  }, [clientId, selectedBusinessUnitId]);

  const fetchMentionableUsers = useCallback(async () => {
    try {
      const res = await listMentionableUsers({
        buId: selectedBusinessUnitId || undefined
      });
      setMentionableUsers(res || []);
    } catch (e) {
      console.error("Failed to load mentionable users", e);
    }
  }, [selectedBusinessUnitId]);

  const fetchContacts = useCallback(async () => {
    if (!clientId) return;
    setLoadingContacts(true);
    try {
      const res = await listClientContacts(clientId, {
        buId: selectedBusinessUnitId || undefined
      });
      setContacts(Array.isArray(res) ? res : []);
    } catch (e) {
      toast({
        title: t("pilotClients.detail.toasts.loadContactsFailed"),
        status: "error"
      });
      setContacts([]);
    } finally {
      setLoadingContacts(false);
    }
  }, [clientId, selectedBusinessUnitId, toast, t]);

  const fetchNeeds = useCallback(async () => {
    if (!clientId) return;
    setLoadingNeeds(true);
    try {
      const res = await listClientNeeds(
        {
          clientId: Number(clientId),
          status: status || undefined,
          search: search || undefined,
          page,
          pageSize
        },
        { buId: selectedBusinessUnitId || undefined }
      );
      setNeeds(res.items || []);
      setTotalItems(res.totalItems || 0);
      setTotalPages(
        res.totalPages ||
          Math.max(1, Math.ceil((res.totalItems || 0) / pageSize))
      );
    } catch (e) {
      toast({
        title: t("pilotClients.detail.toasts.loadNeedsFailed"),
        status: "error"
      });
      setNeeds([]);
      setTotalItems(0);
      setTotalPages(1);
    } finally {
      setLoadingNeeds(false);
    }
  }, [clientId, status, search, page, pageSize, selectedBusinessUnitId, toast, t]);

  useEffect(() => {
    if (!isOpen) return;
    if (!isBUContextLoaded) return;
    fetchClient();
  }, [fetchClient, isBUContextLoaded, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setTabIndex(initialTabIndex);
  }, [initialTabIndex, isOpen, clientId]);

  useEffect(() => {
    if (!isOpen) return;
    if (!isBUContextLoaded) return;
    fetchComments();
    fetchMentionableUsers();
  }, [fetchComments, fetchMentionableUsers, isBUContextLoaded, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (!isBUContextLoaded) return;
    fetchContacts();
  }, [fetchContacts, isBUContextLoaded, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (!isBUContextLoaded) return;
    fetchNeeds();
  }, [fetchNeeds, isBUContextLoaded, isOpen]);

  // Reset needs pagination when opening a new client
  useEffect(() => {
    if (!isOpen) return;
    setPage(1);
  }, [isOpen, clientId]);

  const handleContactDelete = async (contactId) => {
    try {
      await deleteClientContact(clientId, contactId, {
        buId: selectedBusinessUnitId || undefined
      });
      toast({
        title: t("pilotClients.detail.toasts.contactDeleted"),
        status: "success"
      });
      fetchContacts();
    } catch (e) {
      toast({
        title: t("pilotClients.detail.toasts.deleteFailedTitle"),
        description:
          e?.response?.data?.message ||
          t("pilotClients.detail.toasts.deleteFailedDescription"),
        status: "error"
      });
    }
  };

  const handleContactSuccess = () => {
    fetchContacts();
  };

  const safeClose = () => {
    onClose?.();
    // If this modal is rendered in the dedicated route, we want closing it to go back to the list.
    // In the list page context, the parent passes shouldNavigateOnClose={false} so this no-ops.
    if (shouldNavigateOnClose) {
      try {
        navigate("/portal/asap-pilot/clients");
      } catch {
        // ignore
      }
    }
  };

  const contactColumns = [
    { key: "name", label: t("pilotClients.detail.contacts.table.name"), width: "16%" },
    { key: "title", label: t("pilotClients.detail.contacts.table.title"), width: "14%" },
    { key: "division", label: t("pilotClients.detail.contacts.table.division"), width: "14%" },
    { key: "status", label: t("pilotClients.detail.contacts.table.status"), width: "14%" },
    { key: "email", label: t("pilotClients.detail.contacts.table.email"), width: "16%" },
    { key: "phone", label: t("pilotClients.detail.contacts.table.phone"), width: "12%" },
    { key: "linkedin", label: t("pilotClients.detail.contacts.table.linkedin"), width: "10%" },
    { key: "actions", label: t("pilotClients.detail.contacts.table.actions"), width: "9%", align: "right" },
  ];

  const needColumns = [
    { key: "title", label: t("pilotClients.detail.needs.table.title"), width: "26%" },
    { key: "status", label: t("pilotClients.detail.needs.table.status"), width: "14%" },
    { key: "owner", label: t("pilotClients.detail.needs.table.owner"), width: "16%" },
    { key: "created", label: t("pilotClients.detail.needs.table.created"), width: "18%" },
    { key: "updated", label: t("pilotClients.detail.needs.table.updated"), width: "18%" },
    { key: "link", label: t("pilotClients.detail.needs.table.link"), width: "8%" },
  ];

  return (
    <>
      <Modal isOpen={isOpen} onClose={safeClose} size="6xl">
        <ModalOverlay bg="rgba(11,31,58,.45)" />
        <ModalContent sx={MODAL_CONTENT_SX}>
          <ModalHeader sx={MODAL_HEADER_SX}>
            <HStack spacing="10px" align="center">
              <Text
                as="span"
                fontSize="17px"
                fontWeight={800}
                letterSpacing="-0.02em"
                color="portal.text"
                isTruncated
                sx={{ marginBottom: "0 !important" }}
              >
                {client?.name || t("pilotClients.detail.titleFallback")}
              </Text>
              {client?.status && (
                <StatusBadge
                  label={getStatusLabel(
                    t,
                    CLIENT_STATUS_LABEL_KEYS,
                    CLIENT_STATUS_LABELS,
                    client.status
                  ) || "-"}
                  colorScheme={CLIENT_STATUS_COLORS[client.status] || "gray"}
                />
              )}
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Tabs
              isFitted
              index={tabIndex}
              onChange={setTabIndex}
            >
              <TabList sx={TAB_LIST_SX}>
                <Tab>{t("pilotClients.detail.tabs.description")}</Tab>
                <Tab>{t("pilotClients.detail.tabs.contacts")}</Tab>
                <Tab>{t("pilotClients.detail.tabs.needs")}</Tab>
                <Tab>{t("pilotClients.detail.tabs.comments")}</Tab>
              </TabList>

              <TabPanels>
                {/* Tab 1: Description */}
                <TabPanel px={0} pt={4}>
                  {topSkills.length > 0 && (
                    <Box
                      bg="portal.panel"
                      borderRadius="10px"
                      borderWidth="1px"
                      borderColor="portal.line"
                      boxShadow="panel"
                      overflow="hidden"
                      mb={3}
                      data-testid="client-top-skills-section"
                    >
                      <Flex
                        align="center"
                        gap={3}
                        minH="44px"
                        px="12px"
                        py="8px"
                        borderBottomWidth="1px"
                        borderColor="portal.line"
                      >
                        <Text
                          fontSize="14px"
                          fontWeight={800}
                          letterSpacing="-0.02em"
                          color="portal.text"
                          sx={{ marginBottom: "0 !important" }}
                        >
                          {t("pilotClients.detail.topSkills.title")}
                        </Text>
                      </Flex>
                      <Box p="12px">
                        <Wrap spacing={2}>
                          {topSkills.slice(0, MAX_TOP_SKILLS_DETAIL).map((skill, idx) => (
                            <WrapItem
                              key={`${skill.name}-${idx}`}
                              data-testid="client-top-skill-chip"
                              data-skill-family={skill.family || ""}
                            >
                              <Tag
                                size="md"
                                colorScheme={familyColor(skill.family)}
                                variant="subtle"
                              >
                                <TagLabel>{skill.name}</TagLabel>
                              </Tag>
                            </WrapItem>
                          ))}
                        </Wrap>
                      </Box>
                    </Box>
                  )}

                  <Box
                    bg="portal.panel"
                    borderRadius="10px"
                    borderWidth="1px"
                    borderColor="portal.line"
                    boxShadow="panel"
                    p="14px"
                  >
                    {loadingClient ? (
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                        {Array.from({ length: 6 }).map((_, i) => (
                          <Box key={`skl-${i}`}>
                            <Box h="10px" w="40%" bg="portal.soft" borderRadius="4px" mb="6px" />
                            <Box h="13px" w="70%" bg="portal.soft" borderRadius="4px" />
                          </Box>
                        ))}
                      </SimpleGrid>
                    ) : !client ? (
                      <EmptyState
                        title={t("pilotClients.detail.companyInfo.notFound")}
                      />
                    ) : (
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                        <Box>
                          <FieldLabel>{t("pilotClients.detail.companyInfo.description")}</FieldLabel>
                          <FieldValue>{client.description || "-"}</FieldValue>
                        </Box>
                        <Box>
                          <FieldLabel>{t("pilotClients.detail.companyInfo.sector")}</FieldLabel>
                          <FieldValue>{client.sector || "-"}</FieldValue>
                        </Box>
                        <Box>
                          <FieldLabel>{t("pilotClients.detail.companyInfo.status")}</FieldLabel>
                          <HStack spacing={2} align="center">
                            {client.status ? (
                              <StatusBadge
                                label={getStatusLabel(
                                  t,
                                  CLIENT_STATUS_LABEL_KEYS,
                                  CLIENT_STATUS_LABELS,
                                  client.status
                                ) || "-"}
                                colorScheme={CLIENT_STATUS_COLORS[client.status] || "gray"}
                              />
                            ) : (
                              <FieldValue>-</FieldValue>
                            )}
                            {canDeleteContact && client.status !== "blacklisted_excluded" && (
                              <Button
                                size="xs"
                                variant="secondary"
                                color="portal.danger"
                                borderColor="portal.danger"
                                _hover={{ bg: "danger.50", borderColor: "portal.danger", color: "portal.danger" }}
                                onClick={async () => {
                                  try {
                                    const { blacklistClient } = await import("../../../api/clientsApi");
                                    await blacklistClient(clientId, { buId: selectedBusinessUnitId || undefined });
                                    toast({ title: t("pilotClients.detail.toasts.blacklisted", { defaultValue: "Company blacklisted" }), status: "success" });
                                    fetchClient();
                                  } catch (e) {
                                    toast({ title: t("pilotClients.detail.toasts.blacklistFailed", { defaultValue: "Failed to blacklist company" }), status: "error" });
                                  }
                                }}
                              >
                                {t("pilotClients.detail.actions.blacklist", { defaultValue: "Blacklist" })}
                              </Button>
                            )}
                            {canDeleteContact && client.status === "blacklisted_excluded" && (
                              <Button
                                size="xs"
                                variant="secondary"
                                onClick={async () => {
                                  try {
                                    const { unblacklistClient } = await import("../../../api/clientsApi");
                                    await unblacklistClient(clientId, { buId: selectedBusinessUnitId || undefined });
                                    toast({ title: t("pilotClients.detail.toasts.unblacklisted", { defaultValue: "Blacklist removed" }), status: "success" });
                                    fetchClient();
                                  } catch (e) {
                                    toast({ title: t("pilotClients.detail.toasts.unblacklistFailed", { defaultValue: "Failed to remove blacklist" }), status: "error" });
                                  }
                                }}
                              >
                                {t("pilotClients.detail.actions.unblacklist", { defaultValue: "Remove Blacklist" })}
                              </Button>
                            )}
                          </HStack>
                        </Box>
                        <Box>
                          <FieldLabel>{t("pilotClients.detail.companyInfo.location")}</FieldLabel>
                          <FieldValue>{client.location || "-"}</FieldValue>
                        </Box>
                        <Box>
                          <FieldLabel>{t("pilotClients.detail.companyInfo.numberOfEmployees")}</FieldLabel>
                          <FieldValue>
                            {client.number_of_employees ??
                              client.numberOfEmployees ??
                              "-"}
                          </FieldValue>
                        </Box>
                        <Box>
                          <FieldLabel>{t("pilotClients.detail.companyInfo.manager")}</FieldLabel>
                          <FieldValue>{client.managerName || "-"}</FieldValue>
                        </Box>
                        <Box>
                          <FieldLabel>{t("pilotClients.detail.companyInfo.linkedinUrl")}</FieldLabel>
                          {client.linkedin_url || client.linkedinUrl ? (
                            <Link
                              href={client.linkedin_url || client.linkedinUrl}
                              isExternal
                              color="portal.primary"
                              fontSize="13px"
                              fontWeight="600"
                            >
                              {client.linkedin_url || client.linkedinUrl}
                            </Link>
                          ) : (
                            <FieldValue>-</FieldValue>
                          )}
                        </Box>
                        {hasSkillsTechnical && (
                          <Box>
                            <FieldLabel>
                              {t("pilotClients.detail.companyInfo.skillsTechnical", { defaultValue: "Technical skills" })}
                            </FieldLabel>
                            <FieldValue>{skillsTechnicalText}</FieldValue>
                          </Box>
                        )}
                        {hasSkillsFunctional && (
                          <Box>
                            <FieldLabel>
                              {t("pilotClients.detail.companyInfo.skillsFunctional", { defaultValue: "Functional skills" })}
                            </FieldLabel>
                            <FieldValue>{skillsFunctionalText}</FieldValue>
                          </Box>
                        )}
                        {hasSkillsBusiness && (
                          <Box>
                            <FieldLabel>
                              {t("pilotClients.detail.companyInfo.skillsBusiness", { defaultValue: "Business skills" })}
                            </FieldLabel>
                            <FieldValue>{skillsBusinessText}</FieldValue>
                          </Box>
                        )}
                      </SimpleGrid>
                    )}
                  </Box>
                </TabPanel>

                {/* Tab 2: Contacts */}
                <TabPanel px={0} pt={4}>
                  <Flex align="center" justify="space-between" mb={2}>
                    <Text
                      fontSize="14px"
                      fontWeight={800}
                      letterSpacing="-0.02em"
                      color="portal.text"
                      sx={{ marginBottom: "0 !important" }}
                    >
                      {t("pilotClients.detail.contacts.title")}
                    </Text>
                    <Button
                      leftIcon={<AddIcon />}
                      variant="primary"
                      size="sm"
                      onClick={createContactModal.onOpen}
                      isDisabled={!clientId}
                    >
                      {t("pilotClients.detail.contacts.addButton")}
                    </Button>
                  </Flex>

                  <Box
                    bg="portal.panel"
                    borderRadius="10px"
                    borderWidth="1px"
                    borderColor="portal.line"
                    boxShadow="panel"
                    overflow="hidden"
                  >
                    <Box overflowX="auto">
                      <DataTable
                        columns={contactColumns}
                        rows={contacts}
                        loading={loadingContacts}
                        emptyState={
                          <EmptyState
                            icon={<FaUsers />}
                            title={t("pilotClients.detail.contacts.noContacts")}
                          />
                        }
                        renderRow={(contact) => (
                          <Tr key={contact.id}>
                            <Td fontWeight="700">
                              {contact.name || "-"}{" "}
                              {(contact.is_active === false ||
                                contact.active === false) && (
                                <Box
                                  as="span"
                                  ml={2}
                                  fontSize="10px"
                                  fontWeight="800"
                                  textTransform="uppercase"
                                  letterSpacing="0.04em"
                                  color="portal.muted"
                                  bg="portal.soft"
                                  borderWidth="1px"
                                  borderColor="portal.line"
                                  px="6px"
                                  py="2px"
                                  borderRadius="999px"
                                >
                                  {t("pilotClients.detail.contacts.inactiveBadge")}
                                </Box>
                              )}
                            </Td>
                            <Td>{contact.title || "-"}</Td>
                            <Td>{contact.division || "-"}</Td>
                            <Td>
                              {contact.status ? (
                                <StatusBadge
                                  label={getStatusLabel(
                                    t,
                                    CONTACT_STATUS_LABEL_KEYS,
                                    CONTACT_STATUS_LABELS,
                                    contact.status
                                  )}
                                  colorScheme={CONTACT_STATUS_COLORS[contact.status] || "gray"}
                                />
                              ) : (
                                "-"
                              )}
                            </Td>
                            <Td>{contact.email || "-"}</Td>
                            <Td>{contact.phone || "-"}</Td>
                            <Td>
                              {contact.linkedin_url || contact.linkedinUrl ? (
                                <Link
                                  href={
                                    contact.linkedin_url ||
                                    contact.linkedinUrl
                                  }
                                  isExternal
                                  color="portal.primary"
                                  fontWeight="600"
                                >
                                  {contact.linkedin_url ||
                                    contact.linkedinUrl}
                                </Link>
                              ) : (
                                "-"
                              )}
                            </Td>
                            <Td>
                              <HStack justify="flex-end" spacing="6px">
                                <MiniIconButton
                                  aria-label="Edit"
                                  icon={<EditIcon />}
                                  onClick={() => {
                                    setEditingContact(contact);
                                    editContactModal.onOpen();
                                  }}
                                />
                                <MiniIconButton
                                  aria-label="Delete"
                                  icon={<DeleteIcon />}
                                  color="portal.danger"
                                  _hover={{ bg: "danger.50", borderColor: "portal.danger", color: "portal.danger" }}
                                  isDisabled={!canDeleteContact}
                                  onClick={() => {
                                    setDeleteContactId(contact.id);
                                    deleteContactDialog.onOpen();
                                  }}
                                />
                              </HStack>
                            </Td>
                          </Tr>
                        )}
                      />
                    </Box>
                  </Box>
                </TabPanel>

                {/* Tab 3: Needs */}
                <TabPanel px={0} pt={4}>
                  <Text
                    fontSize="14px"
                    fontWeight={800}
                    letterSpacing="-0.02em"
                    color="portal.text"
                    mb={2}
                    sx={{ marginBottom: "8px !important" }}
                  >
                    {t("pilotClients.detail.needs.title")}
                  </Text>

                  <Box
                    bg="portal.panel"
                    borderRadius="10px"
                    borderWidth="1px"
                    borderColor="portal.line"
                    boxShadow="panel"
                    overflow="hidden"
                  >
                    <Flex
                      align="center"
                      gap="8px"
                      flexWrap="wrap"
                      px="12px"
                      py="10px"
                      bg="#FBFCFD"
                      borderBottomWidth="1px"
                      borderColor="portal.line"
                    >
                      <FilterBar>
                        <FilterSearch
                          placeholder={t(
                            "pilotClients.detail.needs.filters.searchPlaceholder"
                          )}
                          value={search}
                          onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(1);
                          }}
                        />
                        <Input
                          maxW="220px"
                          h="31px"
                          fontWeight="600"
                          fontSize="13px"
                          placeholder={t(
                            "pilotClients.detail.needs.filters.statusPlaceholder"
                          )}
                          value={status}
                          onChange={(e) => {
                            setStatus(e.target.value);
                            setPage(1);
                          }}
                        />
                        <Select
                          value={pageSize}
                          onChange={(e) => {
                            setPageSize(Number(e.target.value));
                            setPage(1);
                          }}
                          maxW="120px"
                          h="31px"
                          fontWeight="600"
                          fontSize="13px"
                        >
                          {[10, 20, 50].map((sz) => (
                            <option key={sz} value={sz}>
                              {sz}
                              {t("pilotClients.detail.needs.filters.pageSizeSuffix")}
                            </option>
                          ))}
                        </Select>
                      </FilterBar>
                    </Flex>

                    <Box px="12px" py="8px" borderBottomWidth="1px" borderColor="portal.line">
                      <Text fontSize="12px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                        {loadingNeeds
                          ? t("pilotClients.detail.needs.summary.loading")
                          : t("pilotClients.detail.needs.summary.total", {
                              count: totalItems
                            })}
                      </Text>
                    </Box>

                    <Box overflowX="auto">
                      <DataTable
                        columns={needColumns}
                        rows={needs}
                        loading={loadingNeeds}
                        emptyState={
                          <EmptyState
                            icon={<FaClipboardList />}
                            title={t("pilotClients.detail.needs.table.noNeeds")}
                          />
                        }
                        renderRow={(n) => (
                          <Tr key={n.id}>
                            <Td fontWeight="700">{n.title || n.name || "-"}</Td>
                            <Td>{n.status || "-"}</Td>
                            <Td>{n.ownerName || n.managerName || "-"}</Td>
                            <Td>
                              {n.created_at
                                ? new Date(n.created_at).toLocaleString()
                                : "-"}
                            </Td>
                            <Td>
                              {n.updated_at
                                ? new Date(n.updated_at).toLocaleString()
                                : "-"}
                            </Td>
                            <Td>
                              {n.id ? (
                                <Link
                                  as={RouterLink}
                                  color="portal.primary"
                                  fontWeight="600"
                                  to={`/portal/asap-pilot/needs/${n.id}`}
                                >
                                  {t("pilotClients.detail.needs.table.viewLink")}
                                </Link>
                              ) : (
                                "-"
                              )}
                            </Td>
                          </Tr>
                        )}
                      />
                    </Box>

                    <TablePagination
                      page={page}
                      pageSize={pageSize}
                      totalItems={totalItems}
                      totalPages={totalPages}
                      onPageChange={setPage}
                      onPageSizeChange={(sz) => {
                        setPageSize(sz);
                        setPage(1);
                      }}
                    />
                  </Box>
                </TabPanel>

                {/* Tab 4: Comments */}
                <TabPanel px={0} pt={4}>
                  <Box
                    bg="portal.panel"
                    borderRadius="10px"
                    borderWidth="1px"
                    borderColor="portal.line"
                    boxShadow="panel"
                    p="14px"
                  >
                    <ThreadedComments
                      title={t("pilotClients.detail.comments.title")}
                      comments={comments}
                      isLoading={loadingComments}
                      mentionableUsers={mentionableUsers}
                      emptyLabel={t("pilotClients.detail.comments.noComments")}
                      submitLabel={t("pilotClients.detail.comments.actions.submit")}
                      replyLabel={t("pilotClients.detail.comments.actions.reply")}
                      cancelLabel={t("pilotClients.detail.comments.actions.cancel")}
                      placeholder={t("pilotClients.detail.comments.placeholders.new")}
                      replyPlaceholder={t("pilotClients.detail.comments.placeholders.reply")}
                      onCreate={async ({ text, parentCommentId }) => {
                        await addClientComment(
                          clientId,
                          text,
                          { buId: selectedBusinessUnitId || undefined },
                          parentCommentId
                        );
                      }}
                      onReload={fetchComments}
                      onCreateSuccess={() =>
                        toast({
                          title: t("pilotClients.detail.toasts.commentAdded"),
                          status: "success"
                        })
                      }
                      onCreateError={(e) =>
                        toast({
                          title: t("pilotClients.detail.toasts.commentFailedTitle"),
                          description:
                            e?.response?.data?.message ||
                            t("pilotClients.detail.toasts.commentFailedDescription"),
                          status: "error"
                        })
                      }
                    />
                  </Box>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </ModalBody>
          <ModalFooter sx={MODAL_FOOTER_SX}>
            <Button variant="secondary" size="sm" onClick={safeClose}>
              {t("pilotClients.detail.close")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {clientId && (
        <>
          <ContactFormModal
            isOpen={createContactModal.isOpen}
            onClose={createContactModal.onClose}
            initialData={null}
            clientId={clientId}
            onSuccess={handleContactSuccess}
          />
          <ContactFormModal
            isOpen={editContactModal.isOpen}
            onClose={() => {
              setEditingContact(null);
              editContactModal.onClose();
            }}
            initialData={editingContact}
            clientId={clientId}
            onSuccess={handleContactSuccess}
          />
        </>
      )}
      <ConfirmDialog
        isOpen={deleteContactDialog.isOpen}
        onClose={() => { setDeleteContactId(null); deleteContactDialog.onClose(); }}
        onConfirm={() => handleContactDelete(deleteContactId)}
        title={t("pilotClients.detail.confirmDeleteContactTitle", { defaultValue: "Delete contact?" })}
        body={t("pilotClients.detail.confirmDeleteContact")}
        confirmLabel={t("common.delete", { defaultValue: "Delete" })}
      />
    </>
  );
};

export default ClientDetailModal;
